// Hero card for a single measurement kind: current value, delta vs previous,
// when, and a sparkline.

import type { Measurement } from '@shared/types.ts';
import type { MeasurementKind } from '@shared/constants.ts';
import { KIND_META, fmtDelta, fmtMeasurement } from '../../lib/measure.ts';
import { relAgo } from '../../lib/format.ts';
import { AnimatedNumber } from '../AnimatedNumber.tsx';

type Props = {
  kind: MeasurementKind;
  measurements: Measurement[]; // newest first
  onAdd: () => void;
  /** Per-measurement SDS trail in chronological order. Drives the bottom
   *  badge line — present when the advice tree was able to compute it. */
  sdsTrail?: { sds: number | null }[];
  /** Optional context line shown under the headline. Used for the
   *  birth-z (AGA/SGA/LGA) chip on the Weight card. */
  contextHint?: string | null;
};

export function GrowthCard({ kind, measurements, onAdd, sdsTrail, contextHint }: Props) {
  const meta = KIND_META[kind];
  const accent = `var(--${meta.accent})`;
  const latest = measurements[0];
  const previous = measurements[1];
  const delta = latest && previous ? latest.value - previous.value : 0;

  return (
    <div
      style={{
        padding: '16px 18px',
        borderRadius: 22,
        background: `linear-gradient(180deg, color-mix(in oklab, ${accent} 12%, transparent), transparent 70%), var(--bg-card)`,
        border: `1px solid color-mix(in oklab, ${accent} 22%, var(--border))`,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: accent,
            textTransform: 'uppercase',
            letterSpacing: 0.04,
          }}
        >
          {meta.label}
        </div>
        {latest && (
          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {relAgo(latest.occurred_at)}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {latest ? (
            <>
              <AnimatedNumber
                value={latest.value}
                format={(v) => fmtMeasurement(kind, v)}
                className="lt-serif lt-tabular"
                style={{
                  fontSize: 38,
                  fontWeight: 400,
                  letterSpacing: -0.02,
                  lineHeight: 1,
                  color: 'var(--text-1)',
                  display: 'inline-block',
                }}
              />
              {previous && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: delta > 0 ? 'var(--good)' : delta < 0 ? 'var(--danger)' : 'var(--text-3)',
                  }}
                >
                  {fmtDelta(kind, delta)} since {relAgo(previous.occurred_at)}
                </div>
              )}
              {contextHint && (
                <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-3)' }}>
                  {contextHint}
                </div>
              )}
            </>
          ) : (
            <div style={{ color: 'var(--text-3)', fontSize: 14 }}>No data yet</div>
          )}
        </div>
        <Sparkline values={measurements.slice(0, 12).map((m) => m.value).reverse()} color={accent} width={140} />
      </div>

      <SdsTrail trail={sdsTrail} accent={accent} />

      <button
        type="button"
        className="lt-btn lt-btn-soft"
        style={{ padding: '10px 0', fontSize: 13 }}
        onClick={onAdd}
      >
        Add {meta.label.toLowerCase()}
      </button>
    </div>
  );
}

function SdsTrail({
  trail,
  accent,
}: {
  trail: Props['sdsTrail'];
  accent: string;
}) {
  // Drop nulls (measurements outside the table) so the trail reads clean.
  // We need at least one numeric point to bother rendering.
  const vals = (trail ?? []).map((e) => e.sds).filter((s): s is number => s != null);
  if (!vals.length) return null;
  // Show only the latest value as text — the dot strip already encodes the
  // trail visually, and rendering "+0.38 → +0.42 → +0.57 → +1.06" makes the
  // line too long on a phone-width card.
  const latest = vals[vals.length - 1];
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 11,
        color: 'var(--text-3)',
        marginTop: -4,
      }}
    >
      <span style={{ textTransform: 'uppercase', letterSpacing: 0.04, fontWeight: 600 }}>SDS</span>
      <span className="lt-tabular" style={{ color: 'var(--text-2)' }}>
        {latest >= 0 ? '+' : ''}
        {latest.toFixed(2)}
      </span>
      <SdsBar values={vals} accent={accent} />
    </div>
  );
}

function SdsBar({ values, accent }: { values: number[]; accent: string }) {
  // Tiny horizontal strip: centered at z=0, ±3 SD spans the width. Each
  // measurement is a dot; latest is filled. Helps spot drift at a glance.
  const w = 56;
  const h = 14;
  // Extra right pad so the +3-clamped latest dot doesn't kiss the SVG edge.
  const padX = 4;
  const xOf = (z: number) => ((Math.max(-3, Math.min(3, z)) + 3) / 6) * (w - padX * 2) + padX;
  return (
    <svg width={w} height={h} style={{ flexShrink: 0, overflow: 'visible' }} aria-hidden>
      <line x1={padX} x2={w - padX} y1={h / 2} y2={h / 2} stroke="var(--divider)" strokeWidth={1} />
      <line x1={w / 2} x2={w / 2} y1={3} y2={h - 3} stroke="var(--divider)" strokeWidth={1} />
      {values.map((v, i) => (
        <circle
          key={i}
          cx={xOf(v)}
          cy={h / 2}
          r={i === values.length - 1 ? 3 : 2}
          fill={i === values.length - 1 ? accent : 'var(--text-3)'}
          opacity={i === values.length - 1 ? 1 : 0.55}
        />
      ))}
    </svg>
  );
}

function Sparkline({
  values,
  color,
  width = 80,
}: {
  values: number[];
  color: string;
  width?: number;
}) {
  const w = width;
  const h = 36;
  // Internal padding (px) so the end-dot and stroke don't kiss the SVG edge.
  const padX = 5;
  if (values.length < 2) {
    return <svg width={w} height={h} style={{ flexShrink: 0 }} />;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * (w - padX * 2) + padX;
    const y = h - 4 - ((v - min) / Math.max(1, max - min)) * (h - 8);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const line = `M${pts.join(' L')}`;
  return (
    <svg width={w} height={h} style={{ flexShrink: 0, opacity: 0.85, overflow: 'visible' }}>
      <path d={line} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      {pts.length > 0 && (
        <circle
          cx={pts[pts.length - 1].split(',')[0]}
          cy={pts[pts.length - 1].split(',')[1]}
          r={2.5}
          fill={color}
        />
      )}
    </svg>
  );
}
